/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropTarget, TransformerProps } from "@advapacs/common";
import Transform from "./Transform";

const Transformer: FunctionComponent<TransformerProps> = ({
  title,
  disabled,
  createTransform,
  onRemoveTransform,
  errors,
  onValueChangeHandler,
  value,
  onDropTransform,
  ruleTypes,
  transformType,
  hideCollapse,
  noResult,
  addBtnText,
  titleConfirmRemove,
  isDynamicConditionType = false,
  onHandleScriptModal,
  isDark = false,
}: TransformerProps) => {
  const { t } = useTranslation(["rule"]);

  const [showChildren, setShowChildren] = useState(true);

  const toggleChildren = () => {
    setShowChildren(!showChildren);
  };

  // Event Handlers for Condition Group
  const onAddConditionBtnHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    createTransform();
  };

  return (
    <div className="condition-rule">
      <div className="card group-draggable">
        <div className="card-header header-elements-inline">
          <h5 className="card-title">{title}</h5>
          <div className="pull-right">
            {!disabled && (
              <>
                <button
                  onClick={onAddConditionBtnHandler}
                  type="button"
                  className="add-condition btn btn-sm btn-secondary mr-1"
                >
                  {addBtnText || t("add_transformer")}
                </button>
              </>
            )}
            {!hideCollapse && (
              <div
                tabIndex={0}
                role="button"
                className={`btn btn-sm ${
                  !showChildren ? "icon-rotate-180" : ""
                }`}
                onClick={toggleChildren}
                onKeyDown={toggleChildren}
                data-action="collapse"
              >
                {}
              </div>
            )}
          </div>
        </div>
        {showChildren && (
          <>
            <div className="table pl-2 mb-0 p-2 font-weight-bold">
              {t("type")}
            </div>
            <DropTarget
              className={value.length === 0 ? "" : "drop-target"}
              hoverClass="drop-target-hover"
              onItemDropped={(item: string) => {
                onDropTransform(item, 0);
              }}
            >
              {value.length === 0 ? (
                <div className="table-inline p-2">
                  {!noResult ? t("no_transformers") : noResult}
                </div>
              ) : (
                ""
              )}
            </DropTarget>
            {value.map((transform, index) => (
              <>
                <Transform
                  isDark={isDark}
                  disabled={disabled}
                  errors={errors}
                  id={transform.id}
                  key={transform.id}
                  onHandleScriptModal={onHandleScriptModal}
                  type={transform.type}
                  transform={transform}
                  onRemoveTransform={onRemoveTransform}
                  onValueChangeHandler={onValueChangeHandler}
                  ruleTypes={ruleTypes}
                  usedTypes={value
                    .filter((_, i) => i !== index)
                    .map((v) => v.type)}
                  transformType={transformType}
                  titleConfirmRemove={titleConfirmRemove}
                  isDynamicConditionType={isDynamicConditionType}
                />
                <DropTarget
                  className="drop-target"
                  hoverClass="drop-target-hover"
                  onItemDropped={(item: string) => {
                    onDropTransform(item, index + 1);
                  }}
                />
              </>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default Transformer;
